import { useMemo, useEffect } from 'preact/hooks';
import { Link } from '@nespresso-design-system/components';
import styles from './ProductVariantsSection.module.scss';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import type { Translations } from '../../config/index.ts';
import AboutRangesDrawer from '@/widgets/ProductDetailsHero/drawers/AboutRangesDrawer/AboutRangesDrawer.tsx';
import type { PersistedState } from '@/widgets/ProductDetailsHero/Store.ts';
import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing.ts';
import type { Options } from '@/widgets/ProductDetailsHero/config/index.ts';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import ModelOption from './ModelOption/ModelOption.tsx';
import type { Product, Range } from '@/domain/Product.ts';
import SkeletonText from '@/widgets/components/controls/Skeleton/SkeletonText.tsx';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape.tsx';
import {
  trackRangeConfiguration,
  trackSubscriptionConfiguration
} from '@/widgets/ProductDetailsHero/Tracking.ts';
import { type SubscriptionsByDuration } from '@/domain/Subscription.ts';
import { hasSubscriptions } from '@/domain/Product';

import ErrorBoundary from '@/widgets/components/containers/ErrorBoundary/ErrorBoundary.tsx';

export type ProductVariantsSectionProps = {
  product: Product;
};

export default function ProductVariantsSection({
  product
}: ProductVariantsSectionProps) {
  const { t } = useTranslations<Translations>();

  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();

  const { productRanges = [] } = useWidgetOptions<Options>();

  const rangesWithMeta = useMemo(
    () => getRangesWithMeta(productRanges, product.model?.ranges),
    [productRanges, product.model?.ranges]
  );

  const rangeDetails = useMemo(() => {
    const titleList: string[] = [];
    rangesWithMeta.forEach((range) => {
      titleList.push(range.title);
    });
    return titleList;
  }, [rangesWithMeta]);

  useEffect(() => {
    trackRangeConfiguration(rangeDetails);

    if (
      product?.subscriptionsByDuration &&
      product?.inStock &&
      hasSubscriptions(product)
    ) {
      trackSubscriptionConfiguration(
        product.subscriptionsByDuration as SubscriptionsByDuration
      );
    }
  }, [product, rangeDetails]);

  return (
    <section class={styles.container}>
      <header class={styles.header}>
        <h2 class={styles.heading} id="choose-model">
          {t('productDetailsHero.productVariantsSection.chooseModelLabel')}
        </h2>
        {rangesWithMeta.length !== 0 ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link
            label={t('productDetailsHero.aboutRangesIcon.label')}
            className={styles.aboutRanges}
            onClick={() => setPersistedState({ openModal: 'about-ranges' })}
            type="button"
          />
        ) : null}
      </header>

      {rangesWithMeta.length !== 0 ? (
        rangesWithMeta.map(({ title, range, fromPrice, offerText }) => (
          <ErrorBoundary>
            <ModelOption
              product={product}
              title={title}
              range={range}
              fromPrice={fromPrice}
              offerText={offerText}
            />
          </ErrorBoundary>
        ))
      ) : (
        // Default to showing an option, even if there's no range config, nor if the ranges aren't grouped by model.
        <ModelOption product={product} range={product.range} />
      )}

      <AboutRangesDrawer
        isOpen={openModal.value === 'about-ranges'}
        onClose={() => setPersistedState({ openModal: undefined })}
        ranges={rangesWithMeta}
      />
    </section>
  );
}

ProductVariantsSection.Skeleton = function Skeleton() {
  return (
    <section class={styles.container}>
      <SkeletonText class={styles.skeletonLabel} />
      <SkeletonShape class={styles.skeletonModelOption} />
      <SkeletonShape class={styles.skeletonModelOption} />
    </section>
  );
};

type RangeWithMeta = {
  title: string;
  description: string;
  imageUrl?: string;
  fromPrice?: number;
  offerText?: string;
  range: Range;
  featuresComparison?: { label: string; isIncluded: boolean }[];
};

function getRangesWithMeta(
  productRanges: Options['productRanges'],
  ranges?: Range[]
): RangeWithMeta[] {
  if (!ranges) {
    return [];
  }

  return productRanges
    .map((productRange) => ({
      title: productRange.title,
      description: productRange.description,
      imageUrl: productRange.imageUrl,
      fromPrice:
        Number(productRange.fromPrice) * PRICE_REPRESENTATION_MULTIPLIER,
      offerText: productRange.offerText,
      range: ranges.find((range) => range.category === productRange.category),
      featuresComparison: productRange.featuresComparison
    }))
    .filter((rangeWithMeta) => rangeWithMeta.range) as RangeWithMeta[];
}
